import { ref, reactive } from 'vue';

// Global loading state
const isLoading = ref(false);
const loadingConfig = reactive({
  title: 'Loading',
  subtitle: 'Please wait...',
  currentStage: 'processing',
  completedStages: [],
  stages: [
    {
      key: 'processing',
      title: 'Processing',
      description: 'Processing your request...',
      icon: 'fa-solid fa-cog'
    }
  ]
});

export function useGlobalLoading() {

  /**
   * Show loading modal with custom configuration
   * @param {Object} config - Loading configuration
   * @param {string} [config.title='Loading'] - Modal title
   * @param {string} [config.subtitle='Please wait...'] - Modal subtitle
   * @param {string} [config.currentStage='processing'] - Current stage key
   * @param {string[]} [config.completedStages=[]] - Array of completed stage keys
   * @param {Array<{key: string, title: string, description: string, icon: string}>} [config.stages] - Array of stage definitions
   */
  const showLoading = (config = {}) => {
    // Reset to defaults
    loadingConfig.title = config.title || 'Loading';
    loadingConfig.subtitle = config.subtitle || 'Please wait...';
    loadingConfig.currentStage = config.currentStage || 'processing';
    loadingConfig.completedStages = config.completedStages || [];

    // Set custom stages or use default
    if (config.stages && config.stages.length > 0) {
      loadingConfig.stages = config.stages;
    } else {
      loadingConfig.stages = [
        {
          key: 'processing',
          title: 'Processing',
          description: config.subtitle || 'Processing your request...',
          icon: 'fa-solid fa-cog'
        }
      ];
    }

    isLoading.value = true;
  };

  /**
   * Hide loading modal
   */
  const hideLoading = () => {
    isLoading.value = false;
  };

  /**
   * Hide loading modal with delay
   * @param {number} [delay=1000] - Delay in milliseconds before hiding
   */
  const hideWithDelay = (delay = 1000) => {
    setTimeout(() => {
      isLoading.value = false;
    }, delay);
  };

  /**
   * Update loading stage
   * @param {string} stage - Stage key to set as current
   * @param {string[]} [completedStages=[]] - Array of completed stage keys
   */
  const updateStage = (stage, completedStages = []) => {
    loadingConfig.currentStage = stage;
    loadingConfig.completedStages = completedStages;
  };

  /**
   * Mark stage as completed
   * @param {string} stage - Stage key to mark as completed
   */
  const completeStage = (stage) => {
    if (!loadingConfig.completedStages.includes(stage)) {
      loadingConfig.completedStages.push(stage);
    }
  };

  /**
   * Show transaction saving loading with predefined stages
   */
  const showTransactionSaving = () => {
    showLoading({
      title: 'Saving Transaction',
      subtitle: 'Processing your transaction...',
      currentStage: 'validating',
      completedStages: [],
      stages: [
        {
          key: 'validating',
          title: 'Validating Data',
          description: 'Checking transaction details...',
          icon: 'fa-solid fa-check-circle'
        },
        {
          key: 'categorizing',
          title: 'Auto-Categorizing',
          description: 'Finding the best category...',
          icon: 'fa-solid fa-tags'
        },
        {
          key: 'saving',
          title: 'Saving Transaction',
          description: 'Storing your transaction...',
          icon: 'fa-solid fa-save'
        }
      ]
    });
  };

  /**
   * Show budget saving loading with predefined stages
   */
  const showBudgetSaving = () => {
    showLoading({
      title: 'Saving Budget',
      subtitle: 'Updating your budget configuration...',
      currentStage: 'validating',
      completedStages: [],
      stages: [
        {
          key: 'validating',
          title: 'Validating Budget',
          description: 'Checking budget calculations...',
          icon: 'fa-solid fa-calculator'
        },
        {
          key: 'processing',
          title: 'Processing Changes',
          description: 'Updating budget categories...',
          icon: 'fa-solid fa-cog'
        },
        {
          key: 'saving',
          title: 'Saving Budget',
          description: 'Storing budget configuration...',
          icon: 'fa-solid fa-wallet'
        },
        {
          key: 'syncing',
          title: 'Syncing to Cloud',
          description: 'Backing up to your account...',
          icon: 'fa-solid fa-cloud-upload-alt'
        }
      ]
    });
  };

  /**
   * Show data loading with predefined stages
   */
  const showDataLoading = () => {
    showLoading({
      title: 'Loading Your Data',
      subtitle: 'Retrieving your financial information...',
      currentStage: 'config',
      completedStages: [],
      stages: [
        {
          key: 'config',
          title: 'User Configuration',
          description: 'Loading your preferences and settings',
          icon: 'fa-solid fa-cog'
        },
        {
          key: 'budget',
          title: 'Budget Data',
          description: 'Syncing your income and expenses',
          icon: 'fa-solid fa-wallet'
        },
        {
          key: 'transactions',
          title: 'Transaction History',
          description: 'Loading your recent transactions',
          icon: 'fa-solid fa-receipt'
        },
        {
          key: 'keywords',
          title: 'Category Keywords',
          description: 'Setting up auto-categorization',
          icon: 'fa-solid fa-tags'
        },
        {
          key: 'savings-goals',
          title: 'Savings Goals',
          description: 'Loading your savings objectives',
          icon: 'fa-solid fa-piggy-bank'
        }
      ]
    });
  };

  /**
   * Show natural language processing loading with predefined stages
   */
  const showNaturalLanguageProcessing = () => {
    showLoading({
      title: 'Processing Transaction',
      subtitle: 'Understanding your natural language input...',
      currentStage: 'parsing',
      completedStages: [],
      stages: [
        {
          key: 'parsing',
          title: 'Parsing Description',
          description: 'Understanding your transaction details...',
          icon: 'fa-solid fa-language'
        },
        {
          key: 'extracting',
          title: 'Extracting Data',
          description: 'Finding amounts, dates, and categories...',
          icon: 'fa-solid fa-search'
        },
        {
          key: 'categorizing',
          title: 'Auto-Categorizing',
          description: 'Assigning the best category...',
          icon: 'fa-solid fa-tags'
        },
        {
          key: 'finalizing',
          title: 'Finalizing',
          description: 'Preparing transaction preview...',
          icon: 'fa-solid fa-check'
        }
      ]
    });
  };

  return {
    // State
    isLoading,
    loadingConfig,

    // Methods
    showLoading,
    hideLoading,
    hideWithDelay,
    updateStage,
    completeStage,

    // Predefined configurations
    showTransactionSaving,
    showBudgetSaving,
    showDataLoading,
    showNaturalLanguageProcessing
  };
}